/**
 * Shared maintenance calculation utilities
 * FT-180: Single source of truth for maintenance stats
 *
 * Used by:
 * - PlannedMaintenanceTab.js (module page stats)
 * - useDashboardCompliance.js (dashboard compliance card)
 * - MaintenanceSummaryCard.js (sidebar summary)
 */

/**
 * Get the next due date from a job record
 * Checks completions array, then job.nextDue, then job.defaultDue
 *
 * @param {Object} job - The maintenance job record
 * @returns {string|null} ISO date string or null
 */
export const getJobNextDue = (job) => {
  const completions = job.completions || [];
  const latestCompletion = completions.length > 0
    ? completions[completions.length - 1]
    : null;
  return latestCompletion?.nextDue || job.nextDue || job.initialNextDue || job.defaultDue || null;
};

/**
 * Check if a job is overdue based on its next due date
 * A job is overdue if its due date is BEFORE today (not equal to today)
 *
 * @param {Object} job - The maintenance job record
 * @param {Date} [today] - Optional today date (defaults to current date, start of day)
 * @returns {boolean}
 */
export const isJobOverdue = (job, today = null) => {
  const nextDue = getJobNextDue(job);
  if (!nextDue) return false;

  const dueDate = new Date(nextDue);
  if (isNaN(dueDate.getTime())) return false;

  // Normalize to start of day
  dueDate.setHours(0, 0, 0, 0);

  const compareDate = today || new Date();
  compareDate.setHours(0, 0, 0, 0);

  // Only jobs PAST their due date are overdue (due TODAY is still compliant)
  return dueDate < compareDate;
};

/**
 * Calculate maintenance statistics from a list of jobs
 * Single source of truth - used by both Dashboard and Maintenance Module
 *
 * This function accepts EITHER:
 * 1. Raw jobs (from storage) - will compute overdue status internally
 * 2. Normalized jobs (with .status already set) - will use existing status
 *
 * @param {Array} jobs - Array of maintenance job records
 * @returns {Object} Stats object with counts and compliance percentage
 *
 * Stats shape:
 * {
 *   overdue: number,     // Jobs past their due date
 *   dueSoon: number,     // Jobs due within warning period (only if normalized)
 *   compliant: number,   // Jobs not overdue or due soon
 *   total: number,       // Total jobs count
 *   compliance: number   // Compliance percentage (0-100)
 * }
 */
export const calculateMaintenanceStats = (jobs = []) => {
  const jobList = Array.isArray(jobs) ? jobs : [];

  if (jobList.length === 0) {
    return {
      overdue: 0,
      dueSoon: 0,
      compliant: 0,
      total: 0,
      compliance: 100
    };
  }

  // Check if jobs are already normalized (have .status property)
  const isNormalized = jobList.some(j => j.status === 'overdue' || j.status === 'due_soon' || j.status === 'compliant');

  let overdueCount, dueSoonCount, compliantCount;

  if (isNormalized) {
    // Jobs already have status computed - just count
    overdueCount = jobList.filter(j => j.status === 'overdue').length;
    dueSoonCount = jobList.filter(j => j.status === 'due_soon').length;
    compliantCount = jobList.filter(j => j.status === 'compliant').length;
  } else {
    // Raw jobs - compute overdue status
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    overdueCount = jobList.filter(job => isJobOverdue(job, today)).length;
    dueSoonCount = 0; // Can't determine due_soon without advanceWarningDays setting
    compliantCount = jobList.length - overdueCount;
  }

  const totalJobs = jobList.length;

  // FT-90: Industry-standard compliance = (total - overdue) / total × 100
  // "Due Soon" jobs are NOT overdue, so they count as compliant
  // This matches MCA/ISM/BIMCO KPI standards: only OVERDUE items are non-compliant
  const compliance = totalJobs > 0
    ? Math.round(((totalJobs - overdueCount) / totalJobs) * 100)
    : 100;

  return {
    overdue: overdueCount,
    dueSoon: dueSoonCount,
    compliant: compliantCount,
    total: totalJobs,
    compliance
  };
};

/**
 * Get overdue jobs from a list
 * Convenience function for filtering
 *
 * @param {Array} jobs - Array of maintenance job records
 * @returns {Array} Overdue jobs only
 */
export const getOverdueJobs = (jobs = []) => {
  const jobList = Array.isArray(jobs) ? jobs : [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Check if normalized
  if (jobList.some(j => j.status === 'overdue')) {
    return jobList.filter(j => j.status === 'overdue');
  }

  // Raw jobs
  return jobList.filter(job => isJobOverdue(job, today));
};
